import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { BUNDLES_SEARCH_DEFAULTS, parseBundlesSearch } from '../../hooks/useBundlesRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
import { useBundle } from '../../hooks/bundles';
import type BundleManagement from '../../components/BundleManagement/index';
import type BundleDetails from '../../components/BundleDetails/index';
import type EditBundle from '../../components/EditBundle/index';

export const bundlesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bundles',
  validateSearch: parseBundlesSearch,
  search: { middlewares: [stripSearchParams(BUNDLES_SEARCH_DEFAULTS)] },
  component: function BundlesIndexRoute() {
    const { currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.bundles'));
    const [Component, setComponent] = useState<typeof BundleManagement | null>(null);
    useEffect(() => {
      import('../../components/BundleManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_bundles')} />;
    }
    return <Component currentUser={currentUser} />;
  },
});

export const bundleDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bundles/$id',
  validateSearch: parseBundlesSearch,
  search: { middlewares: [stripSearchParams(BUNDLES_SEARCH_DEFAULTS)] },
  component: function BundleDetailsRoute() {
    const { currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const listSearch = bundleDetailsRoute.useSearch();
    const bundleId = Number(bundleDetailsRoute.useParams().id);
    const { data: bundle } = useBundle(bundleId);
    useDocumentTitle(bundle?.name ?? t('common.page_titles.bundles'));
    const [Component, setComponent] = useState<typeof BundleDetails | null>(null);
    useEffect(() => {
      import('../../components/BundleDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <DetailsSkeleton label={t('common.loading_bundles')} />;
    }
    return <Component bundleId={bundleId} listSearch={listSearch} currentUser={currentUser} />;
  },
});

export const bundleEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bundles/$id/edit',
  validateSearch: parseBundlesSearch,
  search: { middlewares: [stripSearchParams(BUNDLES_SEARCH_DEFAULTS)] },
  beforeLoad: requireCapability(CAPABILITIES.BUNDLES_WRITE, ({ params }) => ({
    to: '/bundles/$id',
    params: { id: params.id },
    search: true,
  })),
  component: function BundleEditRoute() {
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.bundles'));
    const listSearch = bundleEditRoute.useSearch();
    const bundleId = Number(bundleEditRoute.useParams().id);
    const [Component, setComponent] = useState<typeof EditBundle | null>(null);
    useEffect(() => {
      import('../../components/EditBundle/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <EditSkeleton label={t('common.loading_bundles')} />;
    }
    return <Component bundleId={bundleId} listSearch={listSearch} />;
  },
});
